import mixpanel from 'mixpanel-browser';

// Define a type for valid Mixpanel property values
type MixpanelPropertyValue = 
  | string 
  | number 
  | boolean 
  | Date 
  | Array<string | number> 
  | null 
  | undefined;

// Define a type for Mixpanel properties
type MixpanelProperties = Record<string, MixpanelPropertyValue>;

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;

let mixpanelInitialized = false;

export const initMixpanel = (): void => {
  if (MIXPANEL_TOKEN) {
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: process.env.NODE_ENV === 'development',
      persistence: 'localStorage',
      autocapture: true,
      track_pageview: false
    });
    mixpanelInitialized = true;
  } else {
    console.warn('Mixpanel token is missing! Mixpanel not initialized.');
  }
};

export const trackEvent = (eventName: string, properties?: MixpanelProperties): void => {
  if (MIXPANEL_TOKEN) {
    mixpanel.track(eventName, properties);
    if (process.env.NODE_ENV === 'development') {
      console.log('[Mixpanel DEV] Event:', eventName, properties);
    }
  }
};

export const identifyUser = (userId: string): void => {
  if (MIXPANEL_TOKEN) {
    mixpanel.identify(userId);
    if (process.env.NODE_ENV === 'development') {
      console.log('[Mixpanel DEV] User identified:', userId);
    }
  }
};

export const setUserProperties = (properties: MixpanelProperties): void => {
  if (MIXPANEL_TOKEN) {
    mixpanel.people.set(properties);
    if (process.env.NODE_ENV === 'development') {
      console.log('[Mixpanel DEV] User properties set:', properties);
    }
  }
};

export const setUserPropertiesOnce = (properties: MixpanelProperties): void => {
  if (MIXPANEL_TOKEN) {
    mixpanel.people.set_once(properties);
    if (process.env.NODE_ENV === 'development') {
      console.log('[Mixpanel DEV] User properties set once:', properties);
    }
  }
};

export const incrementUserProperty = (propertyName: string, value: number = 1): void => {
  if (MIXPANEL_TOKEN) {
    mixpanel.people.increment(propertyName, value);
    if (process.env.NODE_ENV === 'development') {
      console.log('[Mixpanel DEV] User property incremented:', propertyName, value);
    }
  }
};

export const resetMixpanel = (): void => {
  if (MIXPANEL_TOKEN) {
    mixpanel.reset();
    if (process.env.NODE_ENV === 'development') {
      console.log('[Mixpanel DEV] Mixpanel reset');
    }
  }
};

// Utility function for translation-related events with common properties
export const trackTranslationEvent = (
  eventName: string, 
  menuId: string, 
  sourceLanguage: string,
  additionalProperties?: MixpanelProperties
): void => {
  const properties = {
    'Menu ID': menuId,
    'Source Language': sourceLanguage,
    ...additionalProperties,
  };
  
  trackEvent(eventName, properties);
};

export const trackApiErrorToast = (
  endpoint?: string,
  errorMessage?: string,
  statusCode?: number,
  // Allow any additional properties that might be useful for specific errors
  additionalProperties?: MixpanelProperties 
) => {
  if (mixpanelInitialized) {
    const properties: MixpanelProperties = { ...additionalProperties };
    if (endpoint) properties["API Endpoint"] = endpoint;
    if (errorMessage) properties["Error Message"] = errorMessage;
    if (statusCode) properties["HTTP Status Code"] = statusCode;

    mixpanel.track("API Error Toast Shown", properties);
  } else {
    console.warn(
      "Mixpanel not initialized. API Error Toast Shown event not tracked:",
      { endpoint, errorMessage, statusCode }
    );
  }
};

export const aliasUser = (userId: string) => {
  if (mixpanelInitialized) {
    mixpanel.alias(userId);
    if (process.env.NODE_ENV === 'development') {
      console.log('[Mixpanel DEV] User alias set:', userId);
    }
  }
};

// Ensure Mixpanel is initialized only once
if (typeof window !== "undefined" && !mixpanelInitialized) {
  initMixpanel();
} 